// An embed as the player reads it, on the `visitor` surface.

import { visitor, withQuery } from '../client'
import type { components, operations } from '../visitor.schema'

export type EmbedView = components['schemas']['EmbedView']
export type EmbedItemEntry = components['schemas']['EmbedItemEntry']
export type EmbedAssetSummary = components['schemas']['AssetSummary']
export type CollectionSummary = components['schemas']['CollectionSummary']
export type PlayerConfig = components['schemas']['PlayerConfig']
export type PlayerThemeView = components['schemas']['PlayerThemeView']
export type AdsConfig = components['schemas']['AdsConfig']
export type OverlayEntry = components['schemas']['OverlayEntry']
export type EmbedQuery = NonNullable<operations['visitor_embed_read']['parameters']['query']>
export type EmbedHeaders = NonNullable<operations['visitor_embed_read']['parameters']['header']>

/** The header the grant travels in, typed from the contract so a rename there fails here. */
const OPENING_HEADER: keyof EmbedHeaders = 'X-Embed-Opening'

/**
 * Everything a player needs to open a publication — its items, its options, its overlays — and a
 * `playback_token` good four hours. Anonymous: the uid admits, plus `password` or `token` where
 * the publication carries one (401 without). A withdrawn, expired or exhausted publication is a
 * 404, one answer for all of them.
 *
 * `opening` is the grant the embed page put in its config: sent as `X-Embed-Opening` — a header,
 * so it stays out of access logs — it names the site the page was embedded on, which a request
 * made from inside the iframe cannot, and stands for a password the page already verified.
 */
export function readEmbed(embedId: string, query: EmbedQuery = {}, opening?: string): Promise<EmbedView> {
  const headers: Record<string, string> = {}
  if (opening) headers[OPENING_HEADER] = opening
  return visitor.get<EmbedView>(withQuery(`/embed/${encodeURIComponent(embedId)}/`, query), headers)
}
